import {
  faCrosshairsSimple,
  faFileInvoice,
  faPencil
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import z from 'zod'
import { useCompany } from '~/api/queries/company'
import type { OrderInfosResponse } from '~/api/queries/order'
import {
  DateInput,
  Dropdown,
  Form,
  InputCol,
  InputLine
} from '~/components/Form'
import { TextInput } from '~/components/Form/Input/TextInput'
import { InputGroup } from '~/components/Form/InputGroup'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import type { StandardModalProps } from '~/components/Global/Design/Modal/StandardModal'
import { StandardModal } from '~/components/Global/Design/Modal/StandardModal'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { profilesListStandardHeight } from '~/components/Global/Elements/ProfilesList/ProfilesList.css'
import { Alert } from '~/components/Global/Interactions/Alert/Alert'
import { AlertVariation } from '~/components/Global/Interactions/Alert/AlertVariation'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { Toggle } from '~/components/Global/Interactions/Toggle/Toggle'
import { CompaniesAutocomplete } from '~/domains/companies/components/CompaniesAutocomplete'
import type { CompanyHead } from '~/domains/companies/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { ContactDropdownOption } from '~/domains/contacts/components/ContactDropdownOption'
import { ContactsList } from '~/domains/contacts/components/ContactsList'
import { findFirstContactOfType } from '~/domains/contacts/contactTypes'
import { useContactHead } from '~/domains/contacts/queries/useContactHead'
import type { ContactHeadData } from '~/domains/contacts/types'
import {
  useCreateMultipleRecoursesMutation,
  useCreateRecourseMutation
} from '~/domains/recourses/mutations/createRecourse'
import { QualificationsDropdown } from '~/domains/taxonomies/components/QualificationsDropdown'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { useSafeNavigateToLinkFromLegacy } from '~/hooks/useSafeNavigateToLinkFromLegacy'
import { toISOLocal } from '~/utils'
import { formatName } from '~/utils/names'
import { useCreateOrderMutation } from '../mutations/createOrder'
import { useDuplicateOrderMutation } from '../mutations/duplicateOrder'
import { MissionAddressField } from './MissionAddressField'
import { companyDisplay, companyName } from './OrderModal.css'

const createOrderSchema = (
  noLimit: boolean,
  requireContact = true,
  requireContractTerm = false,
  requireContractDates = false,
  hasFixedDuration = true
) =>
  z
    .object({
      contact: requireContact
        ? z.string().min(1, 'Le contact est requis')
        : z.string().optional().default(''),
      qualifications: z.union([z.string(), z.array(z.string())]).refine(
        (val) => {
          const arr = Array.isArray(val) ? val : [val]
          return arr.filter(Boolean).length > 0
        },
        { message: 'Au moins une qualification est requise' }
      ),
      jobTitle: z.string().nullish(),
      applicantWantedCount: z.string().nullish(),
      locationRadius: z.coerce.number().nullable().optional(),
      address: z.any().nullable().optional(),
      qualifications_suboption: z
        .record(z.string(), z.array(z.string()))
        .optional(),
      contractTerm: z.string().nullable().optional(),
      contractStart: z.coerce.date().nullable().optional(),
      contractEnd: z.coerce.date().nullable().optional()
    })
    .refine(
      (data) => {
        if (noLimit) return true
        if (!data.applicantWantedCount) return false
        const count = Number.parseInt(data.applicantWantedCount, 10)
        return !Number.isNaN(count) && count >= 1
      },
      {
        message: 'Le nombre de postes doit être au moins égal à 1',
        path: ['applicantWantedCount']
      }
    )
    .refine((data) => !requireContractTerm || !!data.contractTerm, {
      message: 'Le type de contrat est requis',
      path: ['contractTerm']
    })
    .refine((data) => !requireContractDates || !!data.contractStart, {
      message: 'La date de début est requise',
      path: ['contractStart']
    })
    .refine(
      (data) =>
        !requireContractDates || !hasFixedDuration || !!data.contractEnd,
      {
        message: 'La date de fin est requise',
        path: ['contractEnd']
      }
    )

type FormValues = z.infer<ReturnType<typeof createOrderSchema>>

type ResourceType = 'order' | 'recourse'

type CreateOrderModalProps = Omit<
  StandardModalProps,
  'children' | 'buttons' | 'icon' | 'title'
> & {
  companyId?: number
  contactIds?: number[]
  resourceType?: ResourceType
  initialOrderData?: OrderInfosResponse
  orderIdToDuplicate?: number
}

export const CreateOrderModal = ({
  open,
  onClose,
  companyId,
  contactIds,
  resourceType = 'order',
  initialOrderData,
  orderIdToDuplicate
}: CreateOrderModalProps) => {
  const navigateToLinkFromLegacy = useSafeNavigateToLinkFromLegacy()
  const { data: legacyUrl } = useConfig('organization.legacy_url')
  const { data: requireContractTerm } = useConfig(
    'mandatory_order.contract_term'
  )
  const { data: requireContractDates } = useConfig(
    'mandatory_order.contract_dates'
  )
  const isDuplicating = !!initialOrderData && !!orderIdToDuplicate
  const isMultiContact = !!contactIds && contactIds.length > 1
  const [step, setStep] = useState<1 | 2>(
    companyId || (contactIds && contactIds.length > 0) ? 2 : 1
  )
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    companyId ?? null
  )
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    contactIds?.length === 1 ? String(contactIds[0]) : null
  )
  const [noLimit, setNoLimit] = useState(
    initialOrderData?.applicantWantedCount === 0
  )
  const [selectedQualificationsCount, setSelectedQualificationsCount] =
    useState(0)
  const [selectedContractTermId, setSelectedContractTermId] = useState<
    string | null
  >(initialOrderData?.contractTerm?.id.toString() ?? null)

  const { data: contractTerms } = useTaxonomy('contract_terms')
  const currentContractTermId =
    selectedContractTermId ?? initialOrderData?.contractTerm?.id.toString()
  const selectedContractTerm = contractTerms?.find(
    (ct) => String(ct.id) === currentContractTermId
  )
  const hasFixedDuration = selectedContractTerm?.hasFixedDuration ?? true

  const { data: contactData, isLoading: isLoadingContact } = useContactHead<
    ContactHeadData & {
      company: CompanyHead
    }
  >({
    contactId: selectedContactId
      ? Number.parseInt(selectedContactId, 10)
      : undefined,
    additionalFields: ['company']
  })
  const { data: companyData, isLoading: isLoadingCompany } = useCompany(
    selectedCompanyId ?? contactData?.company?.id ?? undefined
  )
  const createOrderMutation = useCreateOrderMutation()
  const createRecourseMutation = useCreateRecourseMutation()
  const createMultipleRecoursesMutation = useCreateMultipleRecoursesMutation()
  const duplicateOrderMutation = useDuplicateOrderMutation()

  const isRecourse = resourceType === 'recourse'

  const companyContacts =
    companyData?.contacts?.filter((c) => !c.archived) || []

  const defaultContact =
    (isRecourse
      ? undefined
      : findFirstContactOfType(companyContacts, 'typeDecisionMaker')) ??
    companyContacts[0]

  useEffect(() => {
    if (!selectedContactId && defaultContact) {
      setSelectedContactId(String(defaultContact.id))
    }
  }, [defaultContact, selectedContactId])

  const handleModifyCompany = () => {
    setStep(1)
    setSelectedCompanyId(null)
    setSelectedContactId(null)
  }

  const handleSubmit = async (data: FormValues) => {
    if (!companyData && !isMultiContact && !isDuplicating) {
      return
    }

    const qualifications = Array.isArray(data.qualifications)
      ? data.qualifications.filter(Boolean)
      : [data.qualifications].filter(Boolean)

    const specialities = [
      ...new Set(Object.values(data.qualifications_suboption ?? {}).flat())
    ]

    const commonPayload = {
      qualifications,
      specialities,
      jobTitle: data.jobTitle || undefined,
      address: data.address ?? undefined,
      locationRadius: data.locationRadius ?? undefined,
      applicantWantedCount: noLimit
        ? 0
        : Number.parseInt(data.applicantWantedCount || '1', 10)
    }

    const contractPayload = {
      contractTerm: data.contractTerm
        ? Number.parseInt(data.contractTerm, 10)
        : undefined,
      contractStart: data.contractStart
        ? toISOLocal(data.contractStart)
        : undefined,
      contractEnd: data.contractEnd ? toISOLocal(data.contractEnd) : undefined
    }

    if (isDuplicating && orderIdToDuplicate) {
      const resource = await duplicateOrderMutation.mutateAsync({
        orderId: orderIdToDuplicate,
        payload: {
          contact: `/v1/contacts/${data.contact}`,
          ...commonPayload,
          ...contractPayload
        }
      })
      onClose()
      navigateToLinkFromLegacy(`/orders/${resource.id}`)
    } else if (isRecourse) {
      if (isMultiContact || qualifications.length > 1) {
        const contacts = isMultiContact
          ? contactIds?.map((id) => `/v1/contacts/${id}`) || []
          : [`/v1/contacts/${data.contact}`]

        await createMultipleRecoursesMutation.mutateAsync({
          contacts,
          ...commonPayload
        })
        onClose()
        if (!isMultiContact && selectedCompanyId) {
          window.location.href = `${legacyUrl ?? ''}/admin/entreprises/edit/${selectedCompanyId}#projet`
        }
      } else {
        const resource = await createRecourseMutation.mutateAsync({
          contact: `/v1/contacts/${data.contact}`,
          ...commonPayload
        })
        onClose()
        window.location.href = `${legacyUrl ?? ''}/admin/recours/modifier/${resource.id}`
      }
    } else {
      const companySegmentations = companyData?.segmentations?.map((s) =>
        String(s.id)
      )

      const resource = await createOrderMutation.mutateAsync({
        contact: `/v1/contacts/${data.contact}`,
        ...commonPayload,
        ...contractPayload,
        ...(companySegmentations?.length
          ? { segmentations: companySegmentations }
          : {})
      })
      onClose()
      navigateToLinkFromLegacy(`/orders/${resource.id}`)
    }
  }

  const selectedContact = selectedContactId
    ? companyData?.contacts?.find(
        (c) => c.id === Number.parseInt(selectedContactId, 10)
      )
    : null
  const contactAddress = selectedContact?.address ?? companyData?.address

  const isMultiRecourse =
    isRecourse && (isMultiContact || selectedQualificationsCount > 1)
  const isRecoursePending =
    createMultipleRecoursesMutation.isPending ||
    createRecourseMutation.isPending

  const step2Buttons = [
    <Button
      key="submit"
      type="submit"
      variation={ButtonVariation.Primary}
      disabled={
        isDuplicating
          ? duplicateOrderMutation.isPending
          : isRecourse
            ? isRecoursePending
            : createOrderMutation.isPending
      }
    >
      {isDuplicating
        ? duplicateOrderMutation.isPending
          ? 'Duplication...'
          : 'Dupliquer la commande'
        : isRecourse
          ? isRecoursePending
            ? 'Création...'
            : isMultiRecourse
              ? 'Créer les recours'
              : 'Créer le recours'
          : createOrderMutation.isPending
            ? 'Création...'
            : 'Créer la commande'}
    </Button>
  ]

  if (
    step === 2 &&
    !isLoadingCompany &&
    !isLoadingContact &&
    (companyContacts.length === 0 || selectedContactId)
  ) {
    return createPortal(
      <Form
        key={selectedCompanyId}
        schema={createOrderSchema(
          noLimit,
          !isMultiContact,
          !isRecourse && Boolean(requireContractTerm),
          !isRecourse && Boolean(requireContractDates),
          hasFixedDuration
        )}
        defaultValues={{
          contact: selectedContactId || '',
          qualifications:
            initialOrderData?.qualifications.map((q) => String(q.id)) || [],
          jobTitle: initialOrderData?.jobTitle || '',
          applicantWantedCount:
            initialOrderData?.applicantWantedCount &&
            initialOrderData.applicantWantedCount > 0
              ? String(initialOrderData.applicantWantedCount)
              : '1',
          address: null,
          locationRadius: 30,
          contractTerm: initialOrderData?.contractTerm?.id.toString() ?? null,
          contractStart: initialOrderData?.contractStart?.match(
            /^\d{4}-\d{2}-\d{2}/
          )
            ? new Date(initialOrderData.contractStart)
            : null,
          contractEnd: initialOrderData?.contractEnd?.match(
            /^\d{4}-\d{2}-\d{2}/
          )
            ? new Date(initialOrderData.contractEnd)
            : null
        }}
        onSubmit={handleSubmit}
      >
        <StandardModal
          open={open}
          onClose={onClose}
          title={
            isDuplicating
              ? 'Dupliquer une commande'
              : isRecourse
                ? isMultiContact
                  ? 'Création groupée de recours'
                  : 'Créer un recours'
                : 'Créer une commande'
          }
          icon={isRecourse ? faCrosshairsSimple : faFileInvoice}
          fixedWidth
          buttons={step2Buttons}
        >
          <InputCol>
            {!isMultiContact && (
              <div className={companyDisplay}>
                <div className={companyName}>
                  <strong>{companyData?.name}</strong>
                  {companyData?.companyStatus?.name && (
                    <Badge
                      title={companyData.companyStatus.name}
                      backgroundColor={companyData.companyStatus.color}
                    />
                  )}
                </div>
                <Button
                  variation={ButtonVariation.Secondary}
                  onClick={handleModifyCompany}
                  buttonSize="small"
                >
                  <FontAwesomeIcon icon={faPencil} /> Modifier l'entreprise
                </Button>
              </div>
            )}
            <InputLine>
              {!isMultiContact ? (
                <Dropdown
                  name="contact"
                  label="Contact"
                  placeholder="Sélectionner un contact"
                  options={companyContacts.map((contact) => ({
                    label: formatName(contact),
                    value: String(contact.id),
                    element: <ContactDropdownOption contact={contact} />
                  }))}
                  required
                  onChange={(value) => {
                    setSelectedContactId(value)
                  }}
                />
              ) : (
                <InputGroup
                  label={`${contactIds.length} contact${contactIds.length > 1 ? 's' : ''} sélectionné${contactIds.length > 1 ? 's' : ''}`}
                  justify="start"
                >
                  <ContactsList
                    contactIds={contactIds}
                    className={profilesListStandardHeight}
                  />
                </InputGroup>
              )}

              <InputGroup
                label={`Nombre de postes ${!isRecourse ? 'ouverts' : 'nécessaires'}`}
              >
                <TextInput
                  name="applicantWantedCount"
                  type="number"
                  standalone
                  min="1"
                  disabled={noLimit}
                  defaultValue="1"
                />
                <Toggle
                  defaultState={noLimit}
                  label="Aucune limite"
                  onChange={(checked) => {
                    setNoLimit(checked)
                  }}
                />
              </InputGroup>
            </InputLine>

            <TextInput
              name="jobTitle"
              label="Intitulé du poste"
              placeholder="Ex: Développeur web"
            />

            <QualificationsDropdown
              name="qualifications"
              label="Qualifications"
              multiple
              required
              withSpecialities
              onChange={(value) => {
                if (isRecourse) {
                  const arr = Array.isArray(value)
                    ? value
                    : value
                      ? [value]
                      : []
                  setSelectedQualificationsCount(arr.filter(Boolean).length)
                }
              }}
            />
            {isRecourse && selectedQualificationsCount > 1 && (
              <Alert
                variation={AlertVariation.Info}
                message={`${selectedQualificationsCount} recours seront créés (un par qualification)${isMultiContact ? ' pour chaque contact' : ''}`}
                onDismiss={false}
              />
            )}

            {!isRecourse && (requireContractTerm || requireContractDates) && (
              <InputLine>
                {requireContractTerm && (
                  <Dropdown
                    label="Type de contrat"
                    name="contractTerm"
                    required
                    options={
                      contractTerms?.map((ct) => ({
                        label: ct.name,
                        value: ct.id.toString()
                      })) ?? []
                    }
                    onChange={setSelectedContractTermId}
                  />
                )}

                {requireContractDates && (
                  <InputGroup align="start" label="Dates du contrat">
                    <DateInput
                      name="contractStart"
                      placeholder="Sélectionner"
                      leadingText={hasFixedDuration ? 'Du' : 'À partir du'}
                      required
                    />
                    {hasFixedDuration && (
                      <DateInput
                        name="contractEnd"
                        placeholder="Sélectionner"
                        leadingText="Au"
                        popperPlacement="bottom-end"
                        required
                      />
                    )}
                  </InputGroup>
                )}
              </InputLine>
            )}

            <MissionAddressField
              contactAddress={contactAddress}
              addressFieldName="address"
              locationRadiusFieldName="locationRadius"
              name="locationAddress"
            />
          </InputCol>
        </StandardModal>
      </Form>,
      document.querySelector('#appWrapper') as HTMLElement
    )
  }

  if (step === 2) {
    return createPortal(
      <StandardModal
        open={open}
        onClose={onClose}
        title={
          isDuplicating
            ? 'Dupliquer une commande'
            : isRecourse
              ? 'Créer un recours'
              : 'Créer une commande'
        }
        icon={faFileInvoice}
        fixedWidth
      >
        <Loader />
      </StandardModal>,
      document.querySelector('#appWrapper') as HTMLElement
    )
  }

  return createPortal(
    <StandardModal
      open={open}
      onClose={onClose}
      title={
        isDuplicating
          ? 'Dupliquer une commande'
          : isRecourse
            ? 'Créer un recours'
            : 'Créer une commande'
      }
      icon={faFileInvoice}
      fixedWidth
    >
      <div>
        <CompaniesAutocomplete
          label="Entreprise"
          placeholder="Rechercher une entreprise"
          onChange={(value) => {
            if (value) {
              setSelectedCompanyId(Number.parseInt(value, 10))
              setStep(2)
            }
          }}
        />
      </div>
    </StandardModal>,
    document.querySelector('#appWrapper') as HTMLElement
  )
}
